/**
 * Lane FX-PORT wave 1, batch A (sword arts): the pieces the sword arts' skill effects share, and the glue that turns an
 * art's effects into ports (render/fx/sampleArt/port.ts `registerPort`). Drawn cell by cell through the FX pen like the
 * approved samples (render/fx/sampleArt/kit.ts, skills.ts): scene grid, FX ramps, 1-px outline on shapes 2+ cells thick,
 * fading by dither level, 12 fps stepped clock. Original shapes (the user's direction 10-11: every skill at the taiji
 * page's quality, anticipation -> strike -> impact -> dissipation, its own identity).
 */
import { add, diamond, hash, isoRing, lerp, line, px, R, unit, type G, type V } from '../kit';
import { registerPort, usePorts, type PortScene, type PortSpec } from '../port';
import type { Sample, Scene } from '../skills';

export const FPS = 12;
const LV = [0, 0.08, 0.2, 1] as const;
/** Dither level 0..3 for what follows (gone, 25 %, 50 % checker, solid). */
export const level = (g: G, l: number): void => {
  g.globalAlpha = LV[Math.max(0, Math.min(3, Math.round(l)))]!;
};
export const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));
/** Whole 12 fps frames since `at` (negative before). */
export const since = (t: number, at: number): number => Math.floor((t - at) * FPS + 1e-6);
/** A tone of a ramp, the index clamped. */
export const C = (ramp: string, i: number): string =>
  R[ramp]![Math.max(0, Math.min(R[ramp]!.length - 1, i))]!;
/** Fade by age in frames: solid, then 50 %, then 25 % (a, b: the frames the steps fall on). */
export const fade = (e: number, a: number, b: number): number => (e < a ? 3 : e < b ? 2 : 1);

// ── An art's effect ──

/** How an effect's afterimages come: the move's own dash copies (effects.ts skillDash), the port's, or none. */
export type Afterimage = 'dash' | 'port' | null;

/** One skill's effect: a sample (drawn on the review page and in the game) with the game timing of its move. */
export interface ArtFx extends Sample {
  /** The game move (SkillDef id). */
  skill: string;
  /** On in the game (main's review passed). */
  on?: boolean;
  /** Sample times: start with no wind-up, the blow, the end, the span played over a wind-up. */
  pre: number;
  hit: number;
  end: number;
  cast?: readonly [number, number];
  /** The dash length on the page (art px): in the game the caster is drawn from where its dash began. */
  dash?: number;
  /** Art px the body steps in over its port afterimages. */
  stepIn?: number;
  /** AI3 (split-body afterimages, the user's selling point): where they come from. */
  afterimage: Afterimage;
  /** What the effect shows, for the page (Thai). */
  note: string;
  /** A set piece's beats (sample s, a Thai label): the page's timeline marks, for the motion lane to match poses to. */
  beats?: readonly { t: number; th: string }[];
}

/** Registers an art's effects as ports (all OFF: main turns each on after review). */
export function portArt(list: readonly ArtFx[]): void {
  usePorts();
  for (const fx of list) {
    const scene = (s: PortScene): Scene => ({ ...s, sample: fx });
    const spec: PortSpec = {
      id: fx.id,
      skills: [fx.skill],
      on: fx.on,
      pre: fx.pre,
      hit: fx.hit,
      end: fx.end,
      cast: fx.cast,
      big: fx.big,
      dash: fx.dash,
      stepIn: fx.stepIn,
      ghosts: fx.afterimage === 'port' ? fx.ghosts : undefined,
      move: fx.move
        ? (s, t): V =>
            fx.move!(
              { ...s, sample: fx, t, body: () => s.a, limbs: () => ({ hand: s.a, handB: s.a, tip: s.a }) },
              t,
            )
        : undefined,
      under: fx.under ? (g, s) => fx.under!(g, scene(s)) : undefined,
      over: (g, s) => fx.over(g, scene(s)),
    };
    registerPort(spec);
  }
}

// ── Pieces ──

/** The point a blow lands on the foe (chest height, a little short of its feet). */
export const strikeAt = (s: Scene, k = 1): V => [s.f[0] - s.d[0] * 3, s.f[1] - s.chest * k];

/** A 4-ray star with diagonals and a diamond core, two frames (`e` 0 big, 1 small). */
export function star(g: G, p: V, e: number, ramp: string, big = 1): void {
  if (e < 0 || e > 1) return;
  const L = Math.round((e === 0 ? 10 : 6) * big);
  const D = Math.round((e === 0 ? 5 : 3) * big);
  level(g, e === 0 ? 3 : 2);
  line(g, [p[0] - L, p[1]], [p[0] + L, p[1]], C('white', 8));
  line(g, [p[0], p[1] - L + 2], [p[0], p[1] + L - 2], C('white', 8));
  for (const [dx, dy] of [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ] as const)
    line(g, [p[0] + dx * 2, p[1] + dy * 2], [p[0] + dx * D, p[1] + dy * D], C(ramp, 4));
  diamond(g, p[0], p[1], e === 0 ? 3 : 2, C(ramp, 4), C('white', 8));
  g.globalAlpha = 1;
}
/** A hit star unless the scene is quiet (a plain blow in a duel bout keeps the small spark only). */
export function hitStar(g: G, s: Scene, p: V, e: number, ramp: string, big = 1): void {
  if (!s.quiet) star(g, p, e, ramp, big);
}

/**
 * A slash band along the blade's real path: the tip's positions from `from` to now (every 1/24 s; in the game the
 * frames the renderer drew, on the page the posed rig's), joined as arcs round the shoulder (angle and reach
 * interpolated, so a swing reads as a curve, a thrust as a straight lunge). The fresh end is `w` cells deep toward the
 * shoulder with a white leading row; the tail thins to one row and dithers away.
 */
export function bladeArc(g: G, s: Scene, from: number, to: number, ramp: string, w = 3, n = 7): void {
  if (s.t < from || s.t >= to) return;
  const b = s.body(s.t);
  const pivot: V = [b[0], b[1] - s.chest * 1.1];
  const pol: [number, number][] = [];
  for (let i = 0; i <= n; i++) {
    const ti = s.t - i / (FPS * 2);
    if (ti < from - 0.05) break;
    const tip = s.limbs(ti).tip;
    pol.push([
      Math.atan2(tip[1] - pivot[1], tip[0] - pivot[0]),
      Math.hypot(tip[0] - pivot[0], tip[1] - pivot[1]),
    ]);
  }
  if (pol.length < 2) return;
  const top = R[ramp]!.length - 1;
  // Unwrap the angles so each step takes the short way round.
  for (let i = 1; i < pol.length; i++) {
    let da = pol[i]![0] - pol[i - 1]![0];
    while (da > Math.PI) da -= Math.PI * 2;
    while (da < -Math.PI) da += Math.PI * 2;
    pol[i]![0] = pol[i - 1]![0] + da;
  }
  for (let i = pol.length - 2; i >= 0; i--) {
    const [a0, r0] = pol[i]!;
    const [a1, r1] = pol[i + 1]!;
    const steps = Math.max(1, Math.ceil(Math.max(Math.abs(a1 - a0) * Math.max(r0, r1), Math.abs(r1 - r0))));
    level(g, i < 2 ? 3 : i < 4 ? 2 : 1);
    for (let k = 0; k <= steps; k++) {
      const u = k / steps;
      const age = (i + u) / Math.max(1, pol.length - 1);
      const rows = Math.max(1, Math.round(w - (w - 1) * Math.min(1, age * 1.4)));
      const a = a0 + (a1 - a0) * u;
      const r = r0 + (r1 - r0) * u;
      for (let q = rows - 1; q >= 0; q--) {
        const rr = r - q;
        const col =
          q === 0 ? (i < 2 ? C('white', 8) : C(ramp, top - 1)) : C(ramp, q === 1 ? top - 2 : top - 3);
        px(g, pivot[0] + Math.cos(a) * rr, pivot[1] + Math.sin(a) * rr, col);
      }
    }
  }
  g.globalAlpha = 1;
}

/**
 * A bamboo leaf: a 4-cell lancet in one of four turns (0 flat, 1 falling diagonal, 2 upright, 3 rising diagonal), the
 * middle two cells lit, the ends a tone down, one bare 1-px line (no outline: it reads as a glint of green).
 */
export function leaf(g: G, x: number, y: number, turn: number, ramp = 'leaf', big = false): void {
  const t = ((turn % 4) + 4) % 4;
  const d: V = t === 0 ? [1, 0] : t === 1 ? [1, 1] : t === 2 ? [0, 1] : [1, -1];
  const top = R[ramp]!.length - 1;
  px(g, x - d[0], y - d[1], C(ramp, top - 2));
  px(g, x, y, C(ramp, top));
  px(g, x + d[0], y + d[1], C(ramp, top - 1));
  if (t !== 2) px(g, x + d[0] * 2, y + d[1] * 2, C(ramp, top - 2));
  // A big leaf: a second, darker row under the lit middle (2 thick there, so the pen outlines it).
  if (big) {
    px(g, x, y + 1, C(ramp, top - 2));
    px(g, x + d[0], y + d[1] + 1, C(ramp, top - 3));
  }
}

/**
 * A wind streak: a 1-cell line from `a` along `dir` for `len` cells, its head curling back on the upper side into a
 * small hook (3/4 of a turn, radius `r`) — the breeze arts' wind mark.
 */
export function windCurl(g: G, a: V, dir: V, len: number, r: number, head: string, body: string): void {
  const d = unit(dir);
  const b = add(a, d, len);
  line(g, a, lerp(a, b, 0.6), body);
  line(g, lerp(a, b, 0.6), b, head);
  if (r < 1) return;
  // Upper side of the streak (screen y down), the hook turning back over it.
  const up: V = d[0] >= 0 ? [d[1], -d[0]] : [-d[1], d[0]];
  const c = add(b, up, r);
  const a0 = Math.atan2(b[1] - c[1], b[0] - c[0]);
  // Moving on from the bottom of the circle: forward, up, then back over the streak.
  const turn = d[0] >= 0 ? -1 : 1;
  const n = Math.max(6, Math.round(r * 5));
  for (let i = 1; i <= n; i++) {
    const a1 = a0 + turn * (i / n) * Math.PI * 1.5;
    const rr = r * (1 - (i / n) * 0.35);
    px(g, c[0] + Math.cos(a1) * rr, c[1] + Math.sin(a1) * rr, i < n / 2 ? head : body);
  }
}

/** Puffs (dust, mist) flung out in an iso ring and dithered away over `life` frames. */
export function puffs(
  g: G,
  c: V,
  e: number,
  n: number,
  r0: number,
  speed: number,
  ramp: string,
  life = 7,
  rise = 0,
  seed = 0,
): void {
  if (e < 0 || e >= life) return;
  const top = R[ramp]!.length - 1;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + hash(i, 5 + seed) * 0.6;
    const r = r0 + e * speed * (0.7 + hash(i, 7 + seed) * 0.6);
    const x = c[0] + Math.cos(a) * r;
    const y = c[1] + Math.sin(a) * r * 0.5 - e * rise;
    const big = e < life / 2;
    level(g, e < life * 0.4 ? 3 : e < life * 0.7 ? 2 : 1);
    g.fillStyle = C(ramp, big ? top - 1 : top - 2);
    g.fillRect(Math.round(x) - 1, Math.round(y) - 1, big ? 3 : 2, big ? 2 : 1);
    if (big) px(g, x, y - 1, C(ramp, top));
  }
  g.globalAlpha = 1;
}

/** A filled iso disc (2:1) at `c`, radius `r`, one tone at dither level `lvl`, a rim tone on its edge row. */
export function isoDisc(g: G, c: V, r: number, fill: string, rim: string | null, lvl: number): void {
  if (r < 1) return;
  for (let y = Math.floor(-r / 2); y <= r / 2; y++)
    for (let x = Math.floor(-r); x <= r; x++) {
      const d = Math.hypot(x, y * 2);
      if (d > r) continue;
      level(g, lvl);
      px(g, c[0] + x, c[1] + y, rim && d > r - 1.6 ? rim : fill);
    }
  g.globalAlpha = 1;
}

/** An iso ring drawn on from angle `a0` over `frac` of a turn (the tell of an area being marked out). */
export function ringOn(g: G, c: V, rx: number, a0: number, frac: number, css: string, thick = false): void {
  if (frac <= 0) return;
  isoRing(g, c[0], c[1], rx, css, thick, a0, a0 + Math.PI * 2 * Math.min(1, frac));
}

/** Iso ground radius (art px, the x half-axis) of `tiles` map tiles (32 x 16 tiles: one tile ~ 22.6 px across). */
export const tilesPx = (tiles: number): number => Math.round(tiles * 16 * Math.SQRT2);

// ── Re-tint (an approved sample in an art's own ramps, its shapes kept) ──

/** A tone map: every tone of ramp `from` to the same place on ramp `to` (by relative position). */
export function rampMap(pairs: Readonly<Record<string, string>>): Map<string, string> {
  const m = new Map<string, string>();
  for (const [from, to] of Object.entries(pairs)) {
    const a = R[from]!;
    const b = R[to]!;
    a.forEach((css, i) => m.set(css, b[Math.round((i * (b.length - 1)) / Math.max(1, a.length - 1))]!));
  }
  return m;
}

/** The context `g` with its fill and stroke tones run through `map` (everything else passed through). */
export function retint(g: G, map: ReadonlyMap<string, string>): G {
  return new Proxy(g, {
    get(t, k) {
      const v = Reflect.get(t, k) as unknown;
      return typeof v === 'function' ? (v as (...a: unknown[]) => unknown).bind(t) : v;
    },
    set(t, k, v) {
      const out = (k === 'fillStyle' || k === 'strokeStyle') && typeof v === 'string' ? (map.get(v) ?? v) : v;
      return Reflect.set(t, k, out);
    },
  });
}

// ── Wind passing through (the rule, user 10-11: no beams, lasers or energy tubes; ranged qi reads as wind) ──

export interface WindPass {
  /** Packets (short streaks), how fast they travel (art px per 12 fps frame), their length, the side-to-side spread. */
  n: number;
  speed: number;
  len: number;
  spread: number;
  /** Frames each packet lives (it dithers out over its last third). */
  life: number;
  ramp: string;
  /** Every second packet carries a leaf at its head. */
  leaves?: boolean;
  seed?: number;
}
/**
 * A gust moving THROUGH a place: short curved streaks (never a line joining caster and foe) launched from `from` along
 * `dir`, staggered over three frames, each a travelling packet that wavers as it goes and curls at its head, white at
 * the head, the art's tone behind, thinning and dithering at its tail; leaves ride some heads. `e` = frames since launch.
 */
export function windPass(g: G, from: V, dir: V, e: number, o: WindPass): void {
  if (e < 0) return;
  const d = unit(dir);
  const side: V = [-d[1], d[0]];
  const top = R[o.ramp]!.length - 1;
  const seed = o.seed ?? 0;
  for (let i = 0; i < o.n; i++) {
    const age = e - (i % 3);
    if (age < 0 || age >= o.life) continue;
    const off = (hash(i, 31 + seed) - 0.5) * o.spread;
    const head = (age + 1) * o.speed * (0.85 + hash(i, 32 + seed) * 0.3);
    const L = o.len * (0.7 + hash(i, 33 + seed) * 0.6);
    const lv = age < o.life * 0.6 ? 3 : age < o.life * 0.85 ? 2 : 1;
    let prev: V | null = null;
    for (let k = Math.max(0, head - L); k <= head; k += 1) {
      const wave = Math.sin(k * 0.13 + i * 1.7) * 1.6;
      const p = add(add(from, d, k), side, off + wave);
      const u = (k - (head - L)) / L;
      if (prev) {
        level(g, Math.min(lv, u < 0.3 ? 2 : 3));
        line(g, prev, p, u > 0.8 ? C('white', 8) : C(o.ramp, u > 0.4 ? top - 1 : top - 2));
      }
      prev = p;
    }
    if (prev && age < o.life * 0.7) {
      // The curl: a small hook turning back over the head.
      level(g, lv);
      for (let q = 1; q <= 5; q++) {
        const a = (q / 5) * Math.PI * 1.2;
        px(
          g,
          prev[0] + d[0] * Math.sin(a) * 2 - side[0] * (1 - Math.cos(a)) * 2,
          prev[1] + d[1] * Math.sin(a) * 2 - side[1] * (1 - Math.cos(a)) * 2,
          C(o.ramp, top - 1),
        );
      }
      if (o.leaves && i % 2 === 0)
        leaf(g, prev[0] - d[0] * 4, prev[1] - d[1] * 4 - 2, age + i, i % 4 ? 'leaf' : o.ramp, true);
    }
  }
  g.globalAlpha = 1;
}
